import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Sparkles } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { openAssistantWithDraft } from '../lib/assistantBus';
import { CUSTOMER_TYPE_OPTIONS, DEFAULT_CALC_INDEX_YEAR } from '../lib/customer-types';
import {
  confidenceFromScore,
  resolveIntakeTemplates,
  scopeMultiplier,
  type IntakeFocus,
  type IntakeScope,
  type IntakeTemplate,
} from '../lib/intake-template-resolver';

function normalizePostalCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

const FOCUS_OPTIONS: Array<{ id: IntakeFocus; label: string; hint: string }> = [
  { id: 'algemeen', label: 'Algemene renovatie', hint: 'Breed voorstel voor standaard renovatiewerk.' },
  { id: 'badkamer', label: 'Badkamer', hint: 'Nadruk op sanitair en badkamerposten.' },
  { id: 'keuken', label: 'Keuken', hint: 'Nadruk op keuken en afbouw.' },
  { id: 'dak', label: 'Dak', hint: 'Dak(afbouw), isolatie en gerelateerde posten.' },
  { id: 'installaties', label: 'Installaties', hint: 'CV, ventilatie, warmtepomp, elektra e.d.' },
  { id: 'verduurzaming', label: 'Verduurzaming', hint: 'Isolatie, labelstap, energiepakketten.' },
  { id: 'transformatie', label: 'Transformatie', hint: 'Functiewijziging en herindeling.' },
  { id: 'onderhoud', label: 'Onderhoud', hint: 'Meerjarig/periodiek onderhoud.' },
  { id: 'compleet', label: 'Compleet project', hint: 'Brede projectbegroting met meerdere onderdelen.' },
];

const SCOPE_OPTIONS: Array<{ id: IntakeScope; label: string; hint: string }> = [
  { id: 'klein', label: 'Klein', hint: 'Compact project; aantallen worden iets teruggeschaald.' },
  { id: 'middel', label: 'Middel', hint: 'Standaard omvang; basisaantallen uit template.' },
  { id: 'groot', label: 'Groot', hint: 'Grotere omvang; aantallen worden opgeschaald.' },
  { id: 'portefeuille', label: 'Portefeuille', hint: 'Meerdere objecten/fasen; sterkere opschaling.' },
];

export function ProjectIntake() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const [name, setName] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState<string>('particulier');
  const [projectTypeId, setProjectTypeId] = useState('renovatie');
  const [buildingTypeId, setBuildingTypeId] = useState('woning');
  const [postalCode, setPostalCode] = useState('');
  const [focus, setFocus] = useState<IntakeFocus>('algemeen');
  const [scope, setScope] = useState<IntakeScope>('middel');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [projectTypeOptions, setProjectTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'renovatie', label: 'Renovatie' },
  ]);
  const [buildingTypeOptions, setBuildingTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'woning', label: 'Woning' },
  ]);
  const [templates, setTemplates] = useState<IntakeTemplate[]>([]);

  const loadContext = useCallback(async () => {
    setTypesLoading(true);
    setTypesError(null);
    try {
      const [pt, bt, tmpls] = await Promise.all([api.getProjectTypes(), api.getBuildingTypes(), api.getTemplates()]);
      if (pt.project_types?.length) setProjectTypeOptions(pt.project_types);
      if (bt.building_types?.length) setBuildingTypeOptions(bt.building_types);
      setTemplates(tmpls);
    } catch {
      setTypesError('Projectinstellingen konden niet geladen worden. Controleer verbinding en probeer opnieuw.');
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  const ranked = useMemo(
    () =>
      resolveIntakeTemplates(templates, {
        customerTypeId,
        projectTypeId,
        buildingTypeId,
        focus,
        scope,
      }),
    [templates, customerTypeId, projectTypeId, buildingTypeId, focus, scope]
  );

  useEffect(() => {
    if (ranked.length === 0) {
      setSelectedTemplateId(null);
      return;
    }
    if (!selectedTemplateId || !ranked.some((r) => r.template.template_id === selectedTemplateId)) {
      setSelectedTemplateId(ranked[0].template.template_id);
    }
  }, [ranked, selectedTemplateId]);

  const selectedTemplate = ranked.find((r) => r.template.template_id === selectedTemplateId) ?? null;
  const selectedConfidence = selectedTemplate ? confidenceFromScore(selectedTemplate.score) : null;

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Vul een projectnaam in.');
      return;
    }
    if (!selectedTemplateId) {
      setError('Kies eerst een voorstel.');
      return;
    }

    const picked = ranked.find((r) => r.template.template_id === selectedTemplateId)?.template;
    if (!picked) {
      setError('Ongeldige templatekeuze. Kies opnieuw.');
      return;
    }
    if ((picked.project_type ?? '').trim() !== projectTypeId) {
      setError('Template past niet bij het gekozen projecttype.');
      return;
    }

    const pc = normalizePostalCode(postalCode);
    if (pc && !/^\d{4}[A-Z]{2}$/.test(pc)) {
      setError('Postcode ongeldig (gebruik formaat 1234AB).');
      return;
    }

    setCreating(true);
    try {
      let regionId: string | undefined;
      if (pc) {
        try {
          const s = await api.suggestRegionFromPostalCode(pc);
          regionId = s.region_id;
        } catch {
          // fallback zonder regio
        }
      }

      const templatePayload = await api.createProjectFromTemplate({
        template_id: selectedTemplateId,
        tenant_id: tenantId,
      });

      const multiplier = scopeMultiplier(scope);
      const systems = templatePayload.systems.map((s) => ({
        system_id: s.system_id,
        variant_id: s.variant_id,
        quantity: Number((Math.max(0.01, s.quantity * multiplier)).toFixed(2)),
      }));

      const rec = await api.createProject({
        tenant_id: tenantId,
        template_id: selectedTemplateId,
        metadata: { name: name.trim() },
        project_type: projectTypeId,
        building_type: buildingTypeId,
        systems,
        context: {
          customer_type: customerTypeId,
          postal_code: pc || undefined,
          index_year: DEFAULT_CALC_INDEX_YEAR,
          ...(regionId ? { region_id: regionId } : {}),
        },
      });

      navigate(`/projects/${rec.project_id}/posten?intake=1`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Aanmaken van intakeproject is mislukt.');
    } finally {
      setCreating(false);
    }
  };

  const customerLabel = CUSTOMER_TYPE_OPTIONS.find((o) => o.id === customerTypeId)?.label ?? customerTypeId;
  const projectTypeLabel = projectTypeOptions.find((o) => o.id === projectTypeId)?.label ?? projectTypeId;
  const buildingTypeLabel = buildingTypeOptions.find((o) => o.id === buildingTypeId)?.label ?? buildingTypeId;

  return (
    <div className="animate-page-enter pb-16">
      <SeoMeta
        path="/projects/intake"
        noindex
        title="Begeleid nieuw dossier"
        description="Begeleide intake voor een nieuw CalqTool™-dossier: klant- en projecttype, passend startvoorstel uit de bibliotheek en voorgevulde posten — sneller dan vanaf nul."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Nieuw dossier', href: '/projects/new' },
            { label: 'Begeleid starten' },
          ]}
        />
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-text">Begeleid nieuw dossier</h1>
            <p className="mt-1 max-w-2xl text-sm text-text-muted">
              Vul een paar vragen in. CalqTool kiest een passend startvoorstel op basis van{' '}
              <strong className="text-text">klanttype + projecttype</strong> en maakt daarna direct een voorgevuld dossier.
            </p>
          </div>
          <Link
            to="/projects/new"
            className="rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface"
          >
            Handmatig starten
          </Link>
        </div>

        {typesError && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {typesError}
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="rounded-xl border border-border bg-surface-card p-5">
            <h2 className="mb-3 text-base font-semibold text-text">1) Intake</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Projectnaam</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  placeholder="Bijv. Renovatie 12 appartementen - Kade 7"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Klanttype</label>
                <select
                  value={customerTypeId}
                  onChange={(e) => setCustomerTypeId(e.target.value)}
                  disabled={typesLoading}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                >
                  {CUSTOMER_TYPE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-text">Projecttype</label>
                  <select
                    value={projectTypeId}
                    onChange={(e) => setProjectTypeId(e.target.value)}
                    disabled={typesLoading}
                    className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  >
                    {projectTypeOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text">Gebouwtype</label>
                  <select
                    value={buildingTypeId}
                    onChange={(e) => setBuildingTypeId(e.target.value)}
                    disabled={typesLoading}
                    className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  >
                    {buildingTypeOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Postcode (optioneel)</label>
                <input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 font-mono text-text"
                  placeholder="1234 AB"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Type renovatie</label>
                <select
                  value={focus}
                  onChange={(e) => setFocus(e.target.value as IntakeFocus)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                >
                  {FOCUS_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-text-muted">{FOCUS_OPTIONS.find((o) => o.id === focus)?.hint}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Schaal</label>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value as IntakeScope)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                >
                  {SCOPE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-text-muted">{SCOPE_OPTIONS.find((o) => o.id === scope)?.hint}</p>
              </div>
            </div>

            <button
              type="button"
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-text hover:bg-surface-card"
              onClick={() =>
                openAssistantWithDraft(
                  `Help mij een template kiezen voor klanttype "${customerLabel}", projecttype "${projectTypeLabel}", gebouwtype "${buildingTypeLabel}", focus "${focus}" en schaal "${scope}". Geef 2 korte opties met uitleg.`
                )
              }
            >
              <MessageSquare className="h-4 w-4" aria-hidden />
              Assistent meedenken over voorstel
            </button>
          </section>

          <section className="rounded-xl border border-border bg-surface-card p-5">
            <h2 className="mb-2 text-base font-semibold text-text">2) Voorstel (automatisch)</h2>
            <p className="mb-3 text-sm text-text-muted">
              Alleen templates met hetzelfde <strong className="text-text">projecttype</strong> worden meegenomen. Daarna
              rangschikken we op gebouwtype, renovatiefocus en klantprofiel.
            </p>
            {ranked.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Geen passend template gevonden voor deze combinatie. Pas projecttype of gebouwtype aan.
              </div>
            ) : (
              <div className="space-y-2">
                {ranked.slice(0, 4).map((r, idx) => {
                  const checked = selectedTemplateId === r.template.template_id;
                  const confidence = confidenceFromScore(r.score);
                  const confidenceClass =
                    confidence === 'hoog'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                      : confidence === 'middel'
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : 'border-red-200 bg-red-50 text-red-900';
                  return (
                    <label
                      key={r.template.template_id}
                      className={`flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 ${
                        checked ? 'border-primary bg-primary/5' : 'border-border bg-surface'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="intake-template"
                            checked={checked}
                            onChange={() => setSelectedTemplateId(r.template.template_id)}
                          />
                          <span className="text-sm font-medium text-text">{r.template.label}</span>
                          {idx === 0 && (
                            <span className="rounded bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                              Aanbevolen
                            </span>
                          )}
                          <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${confidenceClass}`}>
                            {confidence === 'hoog'
                              ? 'Hoog vertrouwen'
                              : confidence === 'middel'
                                ? 'Middel vertrouwen'
                                : 'Laag vertrouwen'}
                          </span>
                        </span>
                        <span className="text-xs text-text-muted">score {r.score}</span>
                      </div>
                      <p className="text-xs text-text-muted">
                        {r.template.project_type} · {r.template.building_type ?? 'onbekend'} · {r.template.systems.length} posten
                      </p>
                      <p className="text-xs text-text-muted">{r.reasons.slice(0, 2).join(' ')}</p>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedConfidence === 'laag' && (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Deze match heeft laag vertrouwen. Controleer de top 2-3 opties of gebruik handmatig starten.
              </p>
            )}

            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/[0.06] px-3 py-2 text-sm text-text-muted">
              <p className="font-medium text-text">Samenvatting intake</p>
              <p className="mt-1">
                {customerLabel} · {projectTypeLabel} · {buildingTypeLabel} · focus: {focus} · schaal: {scope}
              </p>
            </div>

            {error && (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              disabled={creating || ranked.length === 0 || !selectedTemplate}
              onClick={() => void handleCreate()}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              {creating ? 'Dossier aanmaken…' : 'Maak voorgevuld dossier'}
            </button>
          </section>
        </div>
      </AnimatedSection>
    </div>
  );
}
